import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { getAuthToken } from '../services/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api/v1';

interface ReferralStats {
  parent: number;
  student: number;
  staff: number;
  community: number;
  total: number;
}

const ReferralTrackingBoard: React.FC = () => {
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const token = await getAuthToken();
      const response = await axios.get(`${API_URL}/admissions-game/referral-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching referral stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="p-6">Loading referral stats...</div>;
  }

  if (!stats) {
    return <div className="p-6">No referral data available</div>;
  }

  const referralTypes = [
    { key: 'parent', label: 'Parent Referrals', icon: '👨‍👩‍👧', color: 'bg-blue-500' },
    { key: 'student', label: 'Student Referrals', icon: '🎓', color: 'bg-green-500' },
    { key: 'staff', label: 'Staff Referrals', icon: '👔', color: 'bg-purple-500' },
    { key: 'community', label: 'Community Referrals', icon: '🏘️', color: 'bg-orange-500' }
  ];

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Referral Tracking</h2>
      
      {/* Total Referrals */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg p-6 text-white mb-6">
        <div className="text-sm opacity-90 mb-1">Total Referrals</div>
        <div className="text-4xl font-bold">{stats.total}</div>
      </div>
      
      {/* Referral Type Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {referralTypes.map((type) => (
          <div key={type.key} className="bg-white border rounded-lg p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-3xl">{type.icon}</span>
              <div className={`${type.color} text-white px-3 py-1 rounded-full text-sm font-bold`}>
                {stats[type.key as keyof ReferralStats]}
              </div>
            </div>
            <div className="text-sm font-medium text-gray-700">{type.label}</div>
            <div className="mt-2 text-xs text-gray-500">
              {stats.total > 0 
                ? `${((stats[type.key as keyof ReferralStats] / stats.total) * 100).toFixed(1)}% of total`
                : '0% of total'
              }
            </div>
          </div>
        ))}
      </div>
      
      {/* Points Info */}
      <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <h4 className="font-semibold text-yellow-900 mb-2">⭐ Referral Points</h4>
        <ul className="text-sm text-yellow-800 space-y-1">
          <li>• Regular Lead: 5 points</li>
          <li>• Referral Lead: 20 points</li>
          <li>• Only staff referrals earn wallet credits (₹500 per admission)</li>
          <li>• Parent/Student referrals earn points but no cash rewards</li>
        </ul>
      </div>
    </div>
  );
};

export default ReferralTrackingBoard;
